/* eslint-disable prefer-destructuring */
// Modules
import React, { useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { ErrorResponse } from 'apollo-link-error'
import { useTracking } from 'react-tracking'

// Applications
import { useApp } from 'context'
import { useAccountMDMHook, useLoginHook } from 'hooks'
import { ContactType, StatusMessage } from 'typings'
import { OutputErrorCode, OutputErrorMsg, tracking, ROUTES_PATH } from 'constants/index'
import { trackData } from './tracking'
import { clearStorage, singleSPA } from 'utils'
import { ResendTokenType } from 'typings/account'
import { usePhasedLogin } from 'redesign/hooks/usePhasedLogin'
import { useSignUpStepper } from 'redesign/hooks/useSignUpStepper'
import { defaultConfig } from 'modules/RemoteConfig/defaultConfig'
import { useSignUpAnaytics } from 'hooks/useSignUpAnaytics'

// Components
import { ValidateDestinationToken, ValidateTokenInitial } from 'components/Presenters/ValidateDestinationToken'
import { TemplateContainer } from 'components'
import { SignUpHeader as Header } from 'redesign/components/organisms'
import { useRemoteConfig } from 'modules/RemoteConfig/hooks/useRemoteConfig'

export const MDMValidateToken: React.VFC = () => {
    const history = useHistory()
    const { trackEvent } = useTracking()
    const { openToast } = useApp()
    const { size, setStepValue } = useSignUpStepper()
    const { isFeatureEnabled } = useRemoteConfig()
    const { verifyUser } = useLoginHook()
    const {
        cpf,
        biologicalSex,
        contact,
        password,
        hasMounted,
        setContact,
        resendToken,
        confirmToken,
        resetAccountState,
    } = useAccountMDMHook()
    const { phasedLoginVisibility } = usePhasedLogin()
    const [isLoading, setLoading] = useState<boolean>(false)
    const [isDisableButton, setIsDisableButton] = useState(false)
    const [isLoadingButton, setIsLoadingButton] = useState(false)
    const [fieldState, setFieldState] = useState<StatusMessage>({ status: '', message: '' })
    const newRedirectFlow = isFeatureEnabled('AUTH_NEW_REDIRECT_FLOW')

    const shouldRedirect = !cpf || !biologicalSex || !password

    const { dispatchAnalyticsEvent } = useSignUpAnaytics({
        hasMDM: true,
        defaultDispatchEvent: 'confirmTokenOnOpen',
        shouldCall: hasMounted && !shouldRedirect,
    })

    const SUBTITLE_CONTENT: string = useMemo(() => {
        let subtitle = 'Um código de segurança foi enviado para o [CONTACT_TYPE] <span>[CONTACT_VALUE]</span>.'
        subtitle = subtitle.replace('[CONTACT_VALUE]', contact?.value || '')
        subtitle = subtitle.replace('[CONTACT_TYPE]', contact?.contactType === 'EMAIL' ? 'e-mail' : 'celular')

        return subtitle
    }, [contact])

    useEffect(() => {
        if (size === 3) {
            setStepValue(2)
            return
        }
        setStepValue(3)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    if (!hasMounted) {
        return null
    }

    if (shouldRedirect) {
        history.replace(ROUTES_PATH.SIGN_IN)
        return null
    }

    const handleSubmit = async (values: ValidateTokenInitial) => {
        setIsDisableButton(true)
        setIsLoadingButton(true)
        setFieldState({ status: 'loading', message: 'Validando código' })
        const { token: rawToken } = values
        const token = rawToken.join('')

        const trackinData = trackData({
            isPreRegisterUser: true,
            biologicalSex: biologicalSex,
            contactType: contact?.contactType!,
        })

        sessionStorage.setItem('@LiviaAuth:PhoneToken', token)
        try {
            const successfull = await confirmToken(token)

            trackEvent(trackinData.vpageview)
            trackEvent(trackinData.stepFunnel)

            setFieldState({ status: 'success', message: 'Código validado' })
            clearStorage([
                '@LiviaAuth:SignUpPersonalData',
                '@LiviaAuth:SignUpBiologicalSex',
                '@LiviaAuth:SignUpPostalCode',
                '@LiviaAuth:SignUpPhoneNumber',
                '@LiviaAuth:SignUpUserId',
                '@LiviaAuth:SignUpContact',
                '@LiviaAuth:SignUpPhoneList',
                '@LiviaAuth:SignUpEmailList',
            ])
            if (successfull) {
                const redirectTo = sessionStorage.getItem('@LiviaAuth:Campaigns')
                const historyPayload = {
                    status: 'success',
                    message: 'Cadastro realizado com sucesso!',
                }
                const { isRedirect } = await verifyUser({
                    externalStep: phasedLoginVisibility ? 'PHASED_LOGIN_CADASTRO' : 'LOGIN',
                    loginType: tracking.LOGIN_TYPE.SIGN_UP,
                })

                if (!isRedirect) {
                    setTimeout(() => {
                        if (!newRedirectFlow) {
                            singleSPA.historyPush(redirectTo ?? defaultConfig.strings.AUTH_REDIRECT_LOGIN, historyPayload)
                        } else {
                            singleSPA.historyPush(ROUTES_PATH.FORWARDING_APP)
                        }
                        resetAccountState()
                    }, 600)
                }
                dispatchAnalyticsEvent('signUpSuccess')
                dispatchAnalyticsEvent('confirmTokenOnVerifyToken')
            } else {
                setTimeout(() => {
                    history.push('/login', { status: 'success', message: 'Cadastro realizado com sucesso!' })
                    resetAccountState()
                }, 3600)
            }
        } catch (err) {
            const e: ErrorResponse = err
            if (e.graphQLErrors && e.graphQLErrors.length > 0) {
                const codeError = e.graphQLErrors[0].extensions?.code
                if (codeError === OutputErrorCode.INVALIDTOKEN || codeError === OutputErrorCode.EXPIREDTOKEN) {
                    setIsDisableButton(false)
                    setIsLoadingButton(false)
                    setFieldState({
                        status: 'error',
                        message:
                            OutputErrorCode.INVALIDTOKEN === codeError ? OutputErrorMsg.INVALIDTOKEN : OutputErrorMsg.EXPIREDTOKEN,
                    })
                    dispatchAnalyticsEvent('confirmTokenOnVerifyToken', {
                        errorMessage:
                            OutputErrorCode.INVALIDTOKEN === codeError ? OutputErrorMsg.INVALIDTOKEN : OutputErrorMsg.EXPIREDTOKEN,
                    })

                    return
                }
            }
            setFieldState({ status: '', message: '' })
            openToast('Ocorreu um erro')
            dispatchAnalyticsEvent('confirmTokenOnVerifyToken', { errorMessage: 'Ocorreu um erro' })
        }
    }

    const handleResendTokenModal = async (contactType: ContactType) => {
        try {
            setLoading(true)
            const patientLogin: ResendTokenType = {
                contactId: contact?.id,
                cpf: cpf,
                password: password,
                destination: contactType,
            }
            await resendToken(patientLogin)
            setContact({ ...contact, contactType })
            setLoading(false)
        } catch (error) {
            setLoading(false)
            openToast('Ocorreu um erro')
        }
    }

    return (
        <TemplateContainer>
            <Header showStepper subtitle={SUBTITLE_CONTENT} title="Insira o código" />

            <ValidateDestinationToken
                fieldState={fieldState}
                handleResendToken={handleResendTokenModal}
                handleSubmit={handleSubmit}
                isDestinationMobile={contact?.contactType !== 'EMAIL'}
                isDisableButton={isDisableButton}
                isLoading={isLoading}
                isLoadingButton={isLoadingButton}
            />
        </TemplateContainer>
    )
}
